import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RedirectCommand,
  ResolveFn,
  Router,
} from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { UserAuthService } from 'src/app/@services/user-auth.service';
import { AuthStateService } from 'src/app/@stores/auth-state.service';
import { ExternalRegistrationStateService } from 'src/app/@stores/external-registration-state.service';
import {
  eventScopedCommands,
  eventScopedCommandsWithRegistration,
} from 'src/app/@utils/reports-navigation';
import { UserEnrollSessionService } from '../@services/user-enroll.service';

function isRegistrationViewReportPath(path: string | undefined): boolean {
  return (
    path === 'event/:eventName/:registrationId/view-report' ||
    path === ':eventName/:registrationId/view-report'
  );
}

function preservedRouteCommands(
  router: Router,
  routeConfigPath: string | undefined,
  eventIdentifier: string,
  registrationId: string
): string[] {
  if (isRegistrationViewReportPath(routeConfigPath)) {
    return eventScopedCommandsWithRegistration(
      router,
      eventIdentifier,
      registrationId,
      'view-report'
    );
  }
  return eventScopedCommandsWithRegistration(
    router,
    eventIdentifier,
    registrationId
  );
}

function stripTokenRedirectOrContinue(
  router: Router,
  route: ActivatedRouteSnapshot,
  eventIdentifier: string,
  registrationId: string,
  includeQueryParams: boolean
): RedirectCommand | true {
  if (!route.queryParamMap.has('token')) {
    return true;
  }

  const queryParams = includeQueryParams ? { ...route.queryParams } : {};
  delete queryParams['token'];

  const urlTree = router.createUrlTree(
    preservedRouteCommands(
      router,
      route.routeConfig?.path,
      eventIdentifier,
      registrationId
    ),
    {
      queryParams:
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
    }
  );

  return new RedirectCommand(urlTree, { replaceUrl: true });
}

// TODO: @later Upgrade checklist item: Update usages of Resolve interface to include RedirectCommand in its return type.
export const determineEventRegistrationId: ResolveFn<
  boolean | RedirectCommand
> = (route, _state) => {
  const router = inject(Router);
  const externalRegistrationState = inject(ExternalRegistrationStateService);
  const userSessionService = inject(UserEnrollSessionService);
  const userAuthService = inject(UserAuthService);
  const authStateService = inject(AuthStateService);

  const registrationId = route.paramMap.get('registrationId') ?? '';
  const eventIdentifier = route.paramMap.get('eventName') ?? '';
  const token = route.queryParamMap.get('token');

  const reservedSegments = ['debriefs', 'view-report'];
  if (reservedSegments.includes(registrationId)) {
    return true;
  }

  externalRegistrationState.setRegistrationId(registrationId);

  const createBlockedRedirect = (): RedirectCommand => {
    const urlTree = router.createUrlTree(
      eventScopedCommands(router, eventIdentifier, 'blocked')
    );
    return new RedirectCommand(urlTree, { replaceUrl: true });
  };

  const completeResolve = (includeQueryParams: boolean = false) => {
    const result = stripTokenRedirectOrContinue(
      router,
      route,
      eventIdentifier,
      registrationId,
      includeQueryParams
    );
    return result === true ? true : result;
  };

  // Already authenticated — keep registrationId in the URL; only strip token if present.
  if (authStateService.isValidForEvent(eventIdentifier)) {
    const result = completeResolve(true);
    return result === true ? true : of(result);
  }

  // Handle token authentication
  if (token && userAuthService.isTokenAuthAllowed()) {
    return userAuthService.authenticateWithToken(eventIdentifier, token).pipe(
      switchMap((result) => {
        if (result.success) {
          const userId = authStateService.$userId();
          return userSessionService
            .ensureValidEnrolledUserSession({
              eventNameIdentifier: eventIdentifier,
              userIdentifier: userId || registrationId,
            })
            .pipe(map(() => completeResolve(true)));
        }
        return of(createBlockedRedirect());
      }),
      catchError(() => of(createBlockedRedirect()))
    );
  }

  // Handle registrationId authentication (if authentication is required)
  if (registrationId && userAuthService.isAuthenticationRequired()) {
    return userAuthService
      .authenticateWithRegistrationId(eventIdentifier, registrationId)
      .pipe(
        switchMap((result) => {
          if (result.success) {
            return userSessionService
              .ensureValidEnrolledUserSession({
                eventNameIdentifier: eventIdentifier,
                userIdentifier: registrationId,
              })
              .pipe(map(() => completeResolve(true)));
          }
          return of(createBlockedRedirect());
        }),
        catchError(() => of(createBlockedRedirect()))
      );
  }

  // No authentication required — set up session and keep registrationId in the URL.
  return userSessionService
    .ensureValidEnrolledUserSession({
      eventNameIdentifier: eventIdentifier,
      userIdentifier: registrationId,
    })
    .pipe(map(() => completeResolve(true)));
};
